import { dataTypes } from '@common-packages/shared-constants';
import { DRILL_TYPE } from '@common-packages/drilling';

import {
  defaultColumnDefinition,
  adminColumnDefinition,
  defaultNoDataColumn,
  selectCellEditorParamsFactory,
} from '../shared/columnDefinitions';
import { AdminCellRendererFactory } from '../shared/columnDefinitions/cellRenderers';
import {
  getEditorSelector,
  getDataTypeBasedCellRenderer,
} from '../shared/columnDefinitions/dataTypeBasedAgGridCells.utils';
import { currencyCellFormatter } from '../shared/formatters';
import { dataTypes as ColumnTypes } from '../shared/constants';
import { defaultNativeSelectCellEditorSelector } from '../shared/columnDefinitions/cellEditor';
import { zeroOneToBoolean } from '../utils/zeroOneToBoolean';

import createEditModeCellEditHandlers from '../shared/editMode/createEditModeCellEditHandlers';

import GridHeader from './gridHeader';

export const getDeleteIconOptions = (
  { systemDefined, pending, preventInstanceDeletion },
  deleteRow,
) => {
  if (pending) {
    return {
      shouldShowDeleteIcon: () => false,
    };
  }

  if (systemDefined) {
    return {
      shouldShowDeleteIcon: () => true,
      shouldDisableDeleteIcon: () => true,
      customTooltipMessage: 'This is a system-defined dataset, instances cannot be deleted',
      customIcon: 'desktop',
    };
  }

  if (preventInstanceDeletion) {
    return {
      shouldShowDeleteIcon: () => true,
      shouldDisableDeleteIcon: () => true,
      customTooltipMessage: 'Deletion is disabled for this dataset by content configuration',
    };
  }

  return {
    shouldShowDeleteIcon: () => true,
    onDeleteIconClick: deleteRow,
  };
};

const validators = {
  year: (value, data) => {
    const maxLength = data?.dataItemMetadata?.year.dataLength;
    if (maxLength === 0) {
      return; // unlimited length
    }
    if (value?.length > maxLength) {
      return `Value's length should be less than or equal to ${maxLength}`;
    }
  },
};

const getSelectedCheckboxGroupItem = (data, checkboxGroupItems) =>
  checkboxGroupItems?.find(({ value }) => zeroOneToBoolean(data[value]));

const numericDataTypes = [dataTypes.CURRENCY, dataTypes.INTEGER, dataTypes.RATIO];

const parseNumericCellValue = (dataType, newValue) => {
  if (newValue == null || newValue === '') {
    return '0';
  }

  const stringValue = String(newValue);

  if (dataType === dataTypes.CURRENCY) {
    return stringValue.replace(/[(),\s]/g, '');
  }

  return stringValue;
};

const getColumnDefinitions = ({
  columnsBlueprint,
  isInEditMode,
  updateRow,
  isEditModeActive = () => isInEditMode,
  deleteRow,
  datasetDefinition,
  context,
}) => {
  // fallback for no data to show user "no rows to show"
  if (!columnsBlueprint.length) {
    return [defaultNoDataColumn];
  }

  const getCheckboxGroupValue = ({ value: rowValue, data }, checkboxGroupItems) => {
    const found =
      checkboxGroupItems.find(({ value }) => rowValue === value) ||
      getSelectedCheckboxGroupItem(data, checkboxGroupItems);

    return found?.label;
  };

  const AdminCellRenderer = AdminCellRendererFactory({
    shouldShowEditIcon: () => false,
    ...getDeleteIconOptions(datasetDefinition, deleteRow),
  });

  const columnDefinitions = [
    {
      ...adminColumnDefinition,
      cellRenderer: AdminCellRenderer,
    },
    ...columnsBlueprint.map(
      ({
        dataType,
        displayName,
        field,
        dataItemDefinitionName,
        isEditable,
        drillDestination,
        selectionListId,
        selectionListItems,
        checkboxGroupItems,
      }) => ({
        ...defaultColumnDefinition,
        // Instance order is managed via Reorder; client-side column sort reorders rows on add.
        sortable: false,
        valueFormatter: dataType === dataTypes.CURRENCY && currencyCellFormatter,
        cellStyle: {
          textAlign:
            dataType === dataTypes.STRING || dataType === ColumnTypes.CHECKBOX_GROUP
              ? 'left'
              : 'right',
        },
        headerName: displayName,
        headerComponent: GridHeader,
        headerComponentParams: {
          displayName,
          dataItemName: dataItemDefinitionName,
        },
        valueGetter:
          dataType === ColumnTypes.CHECKBOX_GROUP
            ? ({ data }) => getSelectedCheckboxGroupItem(data, checkboxGroupItems)?.value ?? null
            : dataType === dataTypes.BOOLEAN
            ? ({ data }) => zeroOneToBoolean(data[field])
            : undefined,
        valueSetter: ({ data, newValue }) => {
          if (dataType === ColumnTypes.CHECKBOX_GROUP && checkboxGroupItems?.length) {
            checkboxGroupItems.forEach(({ value }) => {
              data[value] = newValue === value;
            });
          } else if (dataType === dataTypes.BOOLEAN) {
            data[field] = zeroOneToBoolean(newValue);
          } else if (dataType === dataTypes.CURRENCY) {
            data[field] =
              newValue == null || newValue === '' ? '0' : String(newValue).replace(/[(),\s]/g, '');
          } else if (dataType === dataTypes.INTEGER || dataType === dataTypes.RATIO) {
            data[field] = parseNumericCellValue(dataType, newValue);
          } else {
            data[field] = newValue;
          }

          return true;
        },
        ...(numericDataTypes.includes(dataType) && {
          equals: (left, right) => Number(left) === Number(right),
          valueParser: ({ newValue }) => parseNumericCellValue(dataType, newValue),
        }),
        cellRenderer: row =>
          getDataTypeBasedCellRenderer({
            context,
            dataType,
            validators,
            valueFormatted:
              dataType === ColumnTypes.CHECKBOX_GROUP
                ? getCheckboxGroupValue(row, checkboxGroupItems)
                : null,
            getDrillData: () =>
              !isInEditMode &&
              drillDestination && {
                drillType: DRILL_TYPE.MULT_GRID,
                parentDatasetDefinitionId: drillDestination.datasetDefinitionId,
                datasetDefinitionId: drillDestination.childDatasetDefId,
                initialParentDatasetInstanceId: row.data.datasetInstanceId,
              },
          })(row),
        cellEditorSelector: row => {
          if (selectionListId || checkboxGroupItems?.length) {
            return {
              ...defaultNativeSelectCellEditorSelector,
              params: selectCellEditorParamsFactory(
                selectionListId ? selectionListItems : checkboxGroupItems,
                {},
                true,
              )(),
            };
          }
          return getEditorSelector(dataType, validators)(row);
        },
        ...createEditModeCellEditHandlers({
          updateRow,
          isEditModeActive,
        }),
        editable: isInEditMode && isEditable && dataType !== dataTypes.BOOLEAN,
        cellRendererParams: {
          disabled: !isInEditMode,
        },
        field,
        width: 200,
      }),
    ),
  ];

  return columnDefinitions;
};

export default getColumnDefinitions;
